import { useCallback, useState } from 'react';

import { useErrorToast, useSuccessToast } from '../components/Toast';
import { UPLOAD_DOCUMENT_STEPS } from '../components/UploadDocumentModal/steps/constants';
import {
  UPLOAD_DOCUMENT_EXTENSIONS,
  UPLOAD_DOCUMENT_MAX_FILE_SIZE,
} from '../constants/allowed_extensions';
import {
  getUploadedDocumentURL,
  uploadFileToS3,
} from '../services/cloudFilesService';

export function useUploadDocumentFlow(updateUploadedDocument, onCloseModal) {
  const [formData, setFormData] = useState({});
  const [fileAttached, setFileAttached] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [step, setStep] = useState(1);
  const showSuccessToast = useSuccessToast();
  const showErrorToast = useErrorToast();

  const resetFlow = useCallback(() => {
    setFileAttached(null);
    setFormData({});
    setStep(UPLOAD_DOCUMENT_STEPS.UPLOAD_DOCUMENT);
  }, []);

  const raiseError = useCallback(
    (error) => {
      console.error(error);
      resetFlow();
      setFileUploading(false);
      showErrorToast(error?.message || error);
    },
    [resetFlow, showErrorToast],
  );

  const nextStep = useCallback(() => {
    setStep((prev) => prev + 1);
  }, []);

  const onClose = useCallback(() => {
    resetFlow();
    setFormData({ documentTitle: '' });
    setStep(UPLOAD_DOCUMENT_STEPS.UPLOAD_DOCUMENT);
    onCloseModal();
  }, [resetFlow, onCloseModal]);

  const handleFileDrop = useCallback(
    ({ droppedFiles }) => {
      const file = droppedFiles?.[0];
      if (!file) return raiseError('No file attached.');

      const isAllowedFileType = UPLOAD_DOCUMENT_EXTENSIONS.includes(
        file.name.split('.').pop().toLowerCase(),
      );

      if (!isAllowedFileType) {
        return raiseError(`File type for ${file.name} is not supported.`);
      }

      if (file.size > UPLOAD_DOCUMENT_MAX_FILE_SIZE) {
        return raiseError('File size exceeds the 20MB limit.');
      }

      setFileAttached(file);
      setFormData({
        documentTitle: file.name,
        name: file.name,
        file,
      });
      nextStep();
    },
    [nextStep, raiseError],
  );

  const uploadDocument = useCallback(
    async ({ updateData }) => {
      setFileUploading(true);
      try {
        const urlResponse = await getUploadedDocumentURL({
          fileName: updateData.fileName,
        });
        const uploadUrl = urlResponse?.url || urlResponse?.urls?.[0];

        if (!uploadUrl) throw new Error('Missing S3 upload URL');

        await uploadFileToS3(uploadUrl, formData.file);

        await updateUploadedDocument({
          variables: {
            documentId: urlResponse.id,
            ...updateData,
          },
        });

        showSuccessToast('Your document was uploaded!');
        onClose();
      } catch {
        raiseError('Upload failed. Please try again.');
        onClose();
      } finally {
        setFileUploading(false);
      }
    },
    [
      formData.file,
      updateUploadedDocument,
      raiseError,
      showSuccessToast,
      onClose,
    ],
  );

  return {
    step,
    setStep,
    nextStep,
    handleFileDrop,
    setFileAttached,
    fileAttached,
    setFormData,
    formData,
    fileUploading,
    uploadDocument,
    resetFlow,
    onClose,
    setFileUploading,
  };
}
